import { createContext, useContext, useEffect, useState } from 'react'
import {
  checkMaintenanceStatus,
  subscribeToMaintenanceStatus,
  verifyBypassPassword
} from '@/services/maintenanceService'
import {
  setBypassToken,
  clearBypassToken,
  isBypassTokenValid,
  isRateLimitExceeded,
  recordRateLimitAttempt,
  clearRateLimitAttempts
} from '@/config/maintenance'
import CryptoJS from 'crypto-js'

const MaintenanceContext = createContext(null)

export function useMaintenance() {
  const context = useContext(MaintenanceContext)
  if (context === null) {
    console.warn(
      'useMaintenance called outside MaintenanceProvider, returning default values'
    )
    return {
      isMaintenanceMode: false,
      maintenanceMessage: null,
      isAdminBypassed: false,
      isLoading: false,
      verifyBypass: async () => ({ success: false }),
      clearBypass: () => {}
    }
  }
  return context
}

export function MaintenanceProvider({ children }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState(null)
  const [isAdminBypassed, setIsAdminBypassed] = useState(false)
  const [isLoading] = useState(false)

  const shouldBypassMaintenance = () => {
    const hostname = window.location.hostname
    const origin = window.location.origin

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true
    }

    if (
      origin === 'https://digipalz-10512.web.app' ||
      origin === 'https://digipalz-canada.web.app'
    ) {
      return true
    }

    return false
  }

  const applyMaintenanceStatus = (status) => {
    const bypassMaintenance = shouldBypassMaintenance()
    setIsMaintenanceMode(status.isEnabled && !bypassMaintenance)
    setMaintenanceMessage(status.message)

    if (status.isEnabled) {
      setIsAdminBypassed(isBypassTokenValid())
    } else {
      setIsAdminBypassed(false)
      clearBypassToken()
    }
  }

  useEffect(() => {
    let unsubscribe = () => {}
    let cancelled = false

    const runMaintenanceCheck = async () => {
      try {
        const status = await checkMaintenanceStatus()
        if (cancelled) return

        applyMaintenanceStatus(status)

        if (status.fetchSucceeded) {
          unsubscribe = subscribeToMaintenanceStatus((liveStatus) => {
            if (cancelled) return
            applyMaintenanceStatus(liveStatus)
          })
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error checking initial maintenance status:', error)
        }
      }
    }

    const scheduleCheck = () => {
      if (typeof requestIdleCallback === 'function') {
        return requestIdleCallback(() => runMaintenanceCheck(), {
          timeout: 2000
        })
      }
      return setTimeout(runMaintenanceCheck, 0)
    }

    const handle = scheduleCheck()

    return () => {
      cancelled = true
      unsubscribe()
      if (typeof cancelIdleCallback === 'function' && typeof handle === 'number') {
        cancelIdleCallback(handle)
      } else {
        clearTimeout(handle)
      }
    }
  }, [])

  const verifyBypass = async (password) => {
    try {
      if (isRateLimitExceeded()) {
        return {
          success: false,
          error: 'Too many attempts. Please try again later.'
        }
      }

      recordRateLimitAttempt()

      if (!password || typeof password !== 'string' || password.trim() === '') {
        return {
          success: false,
          error: 'Password is required'
        }
      }

      const passwordHash = CryptoJS.SHA256(password).toString()

      if (!passwordHash || passwordHash.trim() === '') {
        console.error('Failed to generate password hash')
        return {
          success: false,
          error: 'Failed to process password. Please try again.'
        }
      }

      const result = await verifyBypassPassword(passwordHash)

      if (result.success && result.token) {
        setBypassToken(result.token)
        setIsAdminBypassed(true)
        clearRateLimitAttempts()
        return { success: true }
      } else {
        return {
          success: false,
          error: result.error || 'Invalid password. Please try again.'
        }
      }
    } catch (error) {
      console.error('Error verifying bypass:', error)
      return {
        success: false,
        error: error.message || 'An error occurred. Please try again.'
      }
    }
  }

  const clearBypass = () => {
    clearBypassToken()
    setIsAdminBypassed(false)
  }

  const value = {
    isMaintenanceMode,
    maintenanceMessage,
    isAdminBypassed,
    isLoading,
    verifyBypass,
    clearBypass
  }

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  )
}
